import { Link, useLocation } from "react-router-dom";

/**
 * Global site footer.
 *
 * Required for Google OAuth verification: privacy + terms + data
 * deletion links must be visible from every public page so reviewers
 * (and users) can find them without hunting.
 *
 * Intentionally hidden on a few full-bleed app screens (map, dashboard
 * editor) where a footer would compete with the live UI. Reviewers
 * land on the landing page first; that's where they look.
 */
const HIDDEN_PREFIXES = [
  "/map",
  "/dashboard/profile-editor",
  "/admin",
];

export default function SiteFooter() {
  const { pathname } = useLocation();
  const hide = HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  if (hide) return null;

  const year = new Date().getFullYear();
  return (
    <footer
      className="border-t border-border bg-muted/40 mt-12"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-6xl px-5 py-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between text-sm">
        <div className="flex flex-col gap-1">
          <div className="font-display font-semibold tracking-tight text-foreground">
            TruckTrackr
          </div>
          <div className="text-xs text-muted-foreground">
            © {year} TruckTrackr. Built in Lancaster, PA.
          </div>
        </div>
        <nav
          aria-label="Footer"
          className="flex flex-wrap gap-x-5 gap-y-2 text-muted-foreground"
        >
          <Link
            to="/privacy"
            className="hover:text-foreground transition-colors"
            data-testid="footer-privacy"
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            className="hover:text-foreground transition-colors"
            data-testid="footer-terms"
          >
            Terms
          </Link>
          <Link
            to="/data-deletion"
            className="hover:text-foreground transition-colors"
            data-testid="footer-data-deletion"
          >
            Data deletion
          </Link>
          <Link
            to="/pricing"
            className="hover:text-foreground transition-colors"
            data-testid="footer-pricing"
          >
            Pricing
          </Link>
          <Link
            to="/features"
            className="hover:text-foreground transition-colors"
            data-testid="footer-features"
          >
            Features
          </Link>
          <Link
            to="/about"
            className="hover:text-foreground transition-colors"
            data-testid="footer-about"
          >
            About
          </Link>
          <Link
            to="/customers"
            className="hover:text-foreground transition-colors"
            data-testid="footer-for-fans"
          >
            For fans
          </Link>
          <Link
            to="/owner-guide"
            className="hover:text-foreground transition-colors"
            data-testid="footer-owner-guide"
          >
            For truck owners
          </Link>
          <Link
            to="/blog"
            className="hover:text-foreground transition-colors"
            data-testid="footer-blog"
          >
            Blog
          </Link>
          <Link
            to="/food-trucks/lancaster-pa"
            className="hover:text-foreground transition-colors"
            data-testid="footer-lancaster"
          >
            Lancaster food trucks
          </Link>
          <Link
            to="/food-trucks/outer-banks-nc"
            className="hover:text-foreground transition-colors"
            data-testid="footer-obx"
          >
            Outer Banks food trucks
          </Link>
          <Link
            to="/food-trucks/bel-air-md"
            className="hover:text-foreground transition-colors"
            data-testid="footer-bel-air"
          >
            Bel Air food trucks
          </Link>
          <Link
            to="/food-trucks/newport-news-va"
            className="hover:text-foreground transition-colors"
            data-testid="footer-newport-news"
          >
            Newport News food trucks
          </Link>
          <Link
            to="/food-trucks/orlando-fl"
            className="hover:text-foreground transition-colors"
            data-testid="footer-orlando"
          >
            Orlando food trucks
          </Link>
          <a
            href="mailto:support@thetrucktrackr.com"
            className="hover:text-foreground transition-colors"
            data-testid="footer-contact"
          >
            Contact
          </a>
        </nav>
      </div>
    </footer>
  );
}
